import React, { useState, useEffect, useCallback } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import api, { timeTrackingAPI, locationTimesAPI } from '../lib/api';
import {
  AlertTriangle,
  Calendar,
  Loader2,
  Download,
  CalendarDays,
  CalendarPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ClockCard } from './timetracking/ClockCard';
import { LocationTimesCard } from './timetracking/LocationTimesCard';
import { TargetHoursCard } from './timetracking/TargetHoursCard';
import { MyBalanceTrendCard } from './timetracking/MyBalanceTrendCard';
import { WeekView } from './timetracking/WeekView';
import { MonthView } from './timetracking/MonthView';
import { CorrectionDialog } from './timetracking/CorrectionDialog';
import { LocationTimeDialog } from './timetracking/LocationTimeDialog';
import { getWeekEnd, getGreeting, isoToLocalHHMM, localDateTimeToUtcIso } from './timetracking/utils';

export default function TimeTracking({ embedded = false }) {
  useAuth();
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - now.getDay() + 1);
    return monday.toISOString().split('T')[0];
  });

  // Correction request state
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [correctionData, setCorrectionData] = useState({
    date: '',
    clock_in: '',
    clock_out: '',
    break_minutes: '',
    reason: '',
    attachment: null, // { filename, original_name, size }
    attachmentUploading: false,
  });
  const [submittingCorrection, setSubmittingCorrection] = useState(false);

  // Monthly view state
  const [activeTab, setActiveTab] = useState('week');
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Location times state (Standort-Zeiten)
  const [locationTimes, setLocationTimes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationCategories, setLocationCategories] = useState([]);
  const [locationTimeDialogOpen, setLocationTimeDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationStartTime, setLocationStartTime] = useState('');
  const [locationEndTime, setLocationEndTime] = useState('');
  const [locationCategory, setLocationCategory] = useState('');
  const [locationNotes, setLocationNotes] = useState('');
  const [savingLocationTime, setSavingLocationTime] = useState(false);

  // GPS requirement
  const [gpsRequired, setGpsRequired] = useState(false);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate live working time
  const getLiveWorkingTime = useCallback(() => {
    const currentStatusValue = status?.status || 'clocked_out';
    const isCurrentlyClockedOut = currentStatusValue === 'clocked_out' || currentStatusValue === 'not_clocked_in' || !status;
    if (!status?.clock_in || isCurrentlyClockedOut) return status?.working_time || 0;
    const clockInTime = new Date(status.clock_in);
    const now = new Date();
    const diffMinutes = Math.floor((now - clockInTime) / 60000);
    const breakMinutes = status?.break_time || 0;
    return Math.max(0, diffMinutes - breakMinutes);
  }, [status]);

  const loadLocationsAndTimes = useCallback(async () => {
    try {
      const [locsRes, timesRes, catsRes] = await Promise.all([
        locationTimesAPI.getAvailableLocations(),
        locationTimesAPI.getToday(),
        locationTimesAPI.getCategories(),
      ]);
      setLocations(locsRes.data);
      setLocationTimes(timesRes.data);
      setLocationCategories(catsRes.data || []);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [statusRes, entriesRes] = await Promise.all([
        api.get('/time-tracking/status'),
        api.get('/time-tracking/my-entries', {
          params: { start_date: selectedWeek, end_date: getWeekEnd(selectedWeek) },
        }),
      ]);
      setStatus(statusRes.data);
      setEntries(entriesRes.data);
    } catch (error) {
      console.error('Failed to load time tracking data:', error);
      toast.error('Fehler beim Laden der Zeitdaten');
    } finally {
      setLoading(false);
    }
  }, [selectedWeek]);

  useEffect(() => {
    loadData();
    loadLocationsAndTimes();
  }, [loadData, loadLocationsAndTimes]);

  const changeWeek = (direction) => {
    const date = new Date(selectedWeek);
    date.setDate(date.getDate() + (direction * 7));
    setSelectedWeek(date.toISOString().split('T')[0]);
  };

  // Load monthly data
  useEffect(() => {
    if (activeTab !== 'month') return;
    const loadMonthlyData = async () => {
      setMonthlyLoading(true);
      try {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth() + 1;
        const response = await timeTrackingAPI.getMonthlyEntries({ year, month });
        setMonthlyData(response.data);
      } catch (error) {
        console.error('Failed to load monthly data:', error);
        toast.error('Fehler beim Laden der Monatsdaten');
      } finally {
        setMonthlyLoading(false);
      }
    };
    loadMonthlyData();
  }, [selectedMonth, activeTab]);

  const changeMonth = (direction) => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedMonth(newDate);
  };

  // GPS requirement check
  useEffect(() => {
    const checkGpsRequirement = async () => {
      try {
        const response = await api.get('/time-tracking/gps-required');
        setGpsRequired(response.data?.gps_required || false);
      } catch {
        setGpsRequired(false);
      }
    };
    checkGpsRequirement();
  }, []);

  // Get GPS location
  const getGPSLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GPS wird nicht unterstützt'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  });

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      let payload = {};
      if (action === 'clock-in' && gpsRequired) {
        try {
          const location = await getGPSLocation();
          payload = { latitude: location.latitude, longitude: location.longitude };
        } catch (gpsError) {
          // GPS ist Pflicht fuer diesen Mitarbeiter — wir DUERFEN den
          // Vorgang nicht ohne Koordinaten weiterfuehren, sonst wird
          // implizit umgangen, dass der Standort GPS verlangt.
          console.warn('GPS-Fehler beim Einstempeln:', gpsError);
          const code = gpsError?.code;
          let msg = 'Standort konnte nicht ermittelt werden. Bitte GPS / Standortzugriff zulassen und erneut versuchen.';
          if (code === 1) {
            msg = 'Standortzugriff wurde verweigert. Bitte in den Browser-Einstellungen zulassen — der Standort verlangt GPS-Pruefung.';
          } else if (code === 3) {
            msg = 'Standortabfrage hat zu lange gedauert. Bitte in einem Bereich mit GPS-Empfang erneut versuchen.';
          }
          toast.error(msg);
          return; // ABORT — kein Einstempeln ohne GPS.
        }
      }
      await api.post(`/time-tracking/${action}`, payload);
      toast.success(
        action === 'clock-in' ? 'Eingestempelt' :
        action === 'clock-out' ? 'Ausgestempelt' :
        action === 'break-start' ? 'Pause gestartet' :
        'Pause beendet'
      );
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Aktion');
    } finally {
      setActionLoading(false);
    }
  };

  // Export entries
  const handleExport = async () => {
    setExporting(true);
    try {
      const startDate = activeTab === 'month' ? format(startOfMonth(selectedMonth), 'yyyy-MM-dd') : selectedWeek;
      const endDate = activeTab === 'month' ? format(endOfMonth(selectedMonth), 'yyyy-MM-dd') : getWeekEnd(selectedWeek);
      const response = await api.get('/time-tracking/my-export', {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `arbeitszeiten_${startDate}_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export erfolgreich');
    } catch {
      toast.error('Fehler beim Export');
    } finally {
      setExporting(false);
    }
  };

  // Location Time Handlers
  const handleAddLocationTime = async () => {
    if (!selectedLocation || !locationStartTime || !locationEndTime || !locationCategory) {
      toast.error('Bitte Standort, Zeitfenster und Tätigkeit angeben');
      return;
    }
    setSavingLocationTime(true);
    try {
      await locationTimesAPI.create({
        location_id: selectedLocation,
        start_time: locationStartTime,
        end_time: locationEndTime,
        category: locationCategory,
        notes: locationNotes || null,
      });
      toast.success('Eintrag erfasst');
      setLocationTimeDialogOpen(false);
      setSelectedLocation('');
      setLocationStartTime('');
      setLocationEndTime('');
      setLocationCategory('');
      setLocationNotes('');
      loadLocationsAndTimes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingLocationTime(false);
    }
  };

  const handleDeleteLocationTime = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await locationTimesAPI.delete(id);
      toast.success('Eintrag gelöscht');
      loadLocationsAndTimes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openCorrectionDialog = (entry) => {
    setSelectedEntry(entry);
    setCorrectionData({
      date: entry.date || '',
      clock_in: isoToLocalHHMM(entry.clock_in),
      clock_out: isoToLocalHHMM(entry.clock_out),
      break_minutes: entry.total_break_minutes != null ? String(entry.total_break_minutes) : '0',
      reason: '',
      attachment: null,
      attachmentUploading: false,
    });
    setCorrectionDialogOpen(true);
  };

  // Neuantrag für einen Tag ohne Buchung (fehlenden Tag nachtragen)
  const openNewDayDialog = () => {
    setSelectedEntry(null);
    setCorrectionData({
      date: '',
      clock_in: '',
      clock_out: '',
      break_minutes: '0',
      reason: '',
      attachment: null,
      attachmentUploading: false,
    });
    setCorrectionDialogOpen(true);
  };

  const handleSubmitCorrection = async () => {
    const isNewDay = !selectedEntry;
    if (!correctionData.reason.trim()) {
      toast.error('Bitte geben Sie einen Grund an');
      return;
    }
    const requestedDate = correctionData.date || selectedEntry?.date;
    if (!requestedDate) {
      toast.error('Bitte ein Datum wählen');
      return;
    }
    if (isNewDay) {
      const today = new Date().toISOString().split('T')[0];
      if (requestedDate > today) {
        toast.error('Nachträge für zukünftige Tage sind nicht möglich');
        return;
      }
      if (!correctionData.clock_in || !correctionData.clock_out) {
        toast.error('Bitte Kommen- und Gehen-Zeit angeben');
        return;
      }
    }
    setSubmittingCorrection(true);
    try {
      const clockIn = localDateTimeToUtcIso(requestedDate, correctionData.clock_in);
      const clockOut = localDateTimeToUtcIso(requestedDate, correctionData.clock_out);
      const breakMinutes = correctionData.break_minutes === '' || correctionData.break_minutes == null
        ? null
        : Math.max(0, parseInt(correctionData.break_minutes, 10) || 0);
      await api.post('/time-tracking/corrections', {
        time_entry_id: isNewDay ? null : selectedEntry.id,
        requested_date: requestedDate,
        requested_clock_in: clockIn,
        requested_clock_out: clockOut,
        requested_break_minutes: breakMinutes,
        attachment_filename: correctionData.attachment?.filename || null,
        attachment_original_name: correctionData.attachment?.original_name || null,
        reason: correctionData.reason,
      });
      toast.success(isNewDay ? 'Nachtrag eingereicht – wartet auf Genehmigung' : 'Korrekturantrag eingereicht');
      setCorrectionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen');
    } finally {
      setSubmittingCorrection(false);
    }
  };

  // Calculate weekly totals
  const weeklyTotals = entries.reduce((acc, entry) => ({
    work: acc.work + (entry.total_work_minutes || 0),
    break: acc.break + (entry.total_break_minutes || 0),
    net: acc.net + (entry.net_work_minutes || 0),
  }), { work: 0, break: 0, net: 0 });

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[300px] rounded-xl" />
          <Skeleton className="h-[400px] rounded-xl" />
        </div>
      </MaybeLayout>
    );
  }

  // Check if user is linked to an employee
  if (status?.status === 'not_linked') {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-6" data-testid="time-tracking-page">
          <h1 className="font-heading font-bold text-3xl text-foreground">Zeiterfassung</h1>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Kein Mitarbeiterprofil verknüpft</p>
                  <p className="text-sm text-amber-700">
                    Ihr Benutzerkonto ist noch nicht mit einem Mitarbeiterprofil verbunden.
                    Bitte kontaktieren Sie Ihren Administrator.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </MaybeLayout>
    );
  }

  const currentStatus = status?.status || 'clocked_out';
  const isWorking = currentStatus === 'clocked_in';
  const isOnBreak = currentStatus === 'on_break';
  const isClockedOut = currentStatus === 'clocked_out' || currentStatus === 'not_clocked_in' || !status;

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6 animate-fade-in" data-testid="time-tracking-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Zeiterfassung</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Guten {getGreeting(currentTime.getHours())}, {status?.employee_name?.split(',').pop()?.trim()}!
            </p>
          </div>
          <div className="text-left sm:text-right w-full sm:w-auto">
            <p className="text-3xl sm:text-4xl font-mono font-bold text-primary leading-none">
              {currentTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              <span className="sm:hidden">
                {currentTime.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
              <span className="hidden sm:inline">
                {currentTime.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </p>
          </div>
        </div>

        {/* Clock Card */}
        <ClockCard
          status={status}
          isWorking={isWorking}
          isOnBreak={isOnBreak}
          isClockedOut={isClockedOut}
          liveWorkingMinutes={getLiveWorkingTime()}
          actionLoading={actionLoading}
          onAction={handleAction}
        />

        {/* Soll-Arbeitszeit (Tag + Woche) */}
        <TargetHoursCard
          targetHours={status?.target_hours}
          todayNetMinutes={isClockedOut ? (status?.entry?.net_work_minutes || 0) : getLiveWorkingTime()}
          weeklyNetMinutes={weeklyTotals.net}
        />

        {/* Mein Stundenkonto (6-Monats-Verlauf) */}
        <MyBalanceTrendCard />

        {/* Location Times - Show when clocked in */}
        {!isClockedOut && (
          <LocationTimesCard
            locationTimes={locationTimes}
            onAdd={() => setLocationTimeDialogOpen(true)}
            onDelete={handleDeleteLocationTime}
          />
        )}

        {/* Weekly/Monthly Overview */}
        <Card className="border-border/60">
          <CardHeader className="px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div>
                <CardTitle className="font-heading text-lg sm:text-xl">Zeitübersicht</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Ihre Arbeitszeiten</CardDescription>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="week" className="gap-1">
                      <Calendar className="h-4 w-4" />
                      Woche
                    </TabsTrigger>
                    <TabsTrigger value="month" className="gap-1">
                      <CalendarDays className="h-4 w-4" />
                      Monat
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openNewDayDialog}
                  className="gap-1"
                  data-testid="add-missing-day-btn"
                  title="Fehlenden Tag ohne Buchung nachtragen"
                >
                  <CalendarPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Tag nachtragen</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={exporting}
                  className="gap-1"
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  <span className="hidden sm:inline">Export</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {activeTab === 'week' && (
              <WeekView
                selectedWeek={selectedWeek}
                onChangeWeek={changeWeek}
                entries={entries}
                weeklyTotals={weeklyTotals}
                onOpenCorrection={openCorrectionDialog}
              />
            )}
            {activeTab === 'month' && (
              <MonthView
                selectedMonth={selectedMonth}
                onChangeMonth={changeMonth}
                monthlyData={monthlyData}
                monthlyLoading={monthlyLoading}
                onOpenCorrection={openCorrectionDialog}
              />
            )}
          </CardContent>
        </Card>

        <CorrectionDialog
          open={correctionDialogOpen}
          onOpenChange={setCorrectionDialogOpen}
          selectedEntry={selectedEntry}
          correctionData={correctionData}
          setCorrectionData={setCorrectionData}
          onSubmit={handleSubmitCorrection}
          submitting={submittingCorrection}
          targetHours={status?.target_hours}
        />

        <LocationTimeDialog
          open={locationTimeDialogOpen}
          onOpenChange={setLocationTimeDialogOpen}
          locations={locations}
          categories={locationCategories}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          startTime={locationStartTime}
          setStartTime={setLocationStartTime}
          endTime={locationEndTime}
          setEndTime={setLocationEndTime}
          category={locationCategory}
          setCategory={setLocationCategory}
          locationNotes={locationNotes}
          setLocationNotes={setLocationNotes}
          onSave={handleAddLocationTime}
          saving={savingLocationTime}
        />
      </div>
    </MaybeLayout>
  );
}
